import {
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Box,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { SvgIconComponent } from '@mui/icons-material';
import { colors } from '@src/styles';
import { dropdownPaperSX, filterFieldSX } from '../scriptsStyles';
import { useState, useEffect } from 'react';

export interface StatusIconConfig {
  icon: SvgIconComponent;
  color: string;
}

interface FiltersBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
}

interface DropdownOptionsFilterProps {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  minWidth?: number;
}

interface CheckboxListFilterProps {
  label: string;
  value: string[];
  options: Array<{ value: string; label: string }>;
  onChange: (value: string[]) => void;
  minWidth?: number;
  iconConfig?: Record<string, StatusIconConfig>;
}

export const DropdownOptionsFilter = ({ label, value, options, onChange, minWidth }: DropdownOptionsFilterProps) => {
  const selectedOption = value ? options.find((opt) => opt.value === value) || null : null;

  return (
    <Autocomplete
      size="small"
      value={selectedOption}
      onChange={(_, newValue) => {
        onChange(newValue?.value || '');
      }}
      options={options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionKey={(option) => option.value}
      clearOnEscape
      openOnFocus
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={label}
          inputProps={{ ...params.inputProps, 'aria-label': label }}
          sx={{ minWidth: minWidth || 170, ...filterFieldSX }}
        />
      )}
      sx={{ minWidth: minWidth || 170 }}
      slotProps={{ paper: { sx: dropdownPaperSX } }}
    />
  );
};

export const CheckboxListFilter = ({
  label,
  value,
  options,
  onChange,
  minWidth,
  iconConfig,
}: CheckboxListFilterProps) => {
  const [localValue, setLocalValue] = useState<string[]>(value);
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    setLocalValue(typeof selectedValue === 'string' ? selectedValue.split(',') : selectedValue);
  };

  const handleClose = () => {
    setIsOpen(false);
    onChange(localValue);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 0)
      return (
        <Box component="span" sx={{ color: colors.brand.textMuted }}>
          {label}
        </Box>
      );

    if (iconConfig) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '24px', overflow: 'hidden' }}>
          {selected.map((val) => {
            const cfg = iconConfig[val];
            if (!cfg) return null;
            const Icon = cfg.icon;
            return (
              <Box
                key={val}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  borderRadius: '50%',
                  backgroundColor: `${cfg.color}20`,
                  border: `1.5px solid ${cfg.color}`,
                }}
              >
                <Icon sx={{ color: cfg.color, fontSize: '12px' }} />
              </Box>
            );
          })}
        </Box>
      );
    }

    return selected
      .map((val) => options.find((opt) => opt.value === val)?.label)
      .filter(Boolean)
      .join(', ');
  };

  return (
    <FormControl variant="outlined" size="small" sx={{ minWidth: minWidth || 150, ...filterFieldSX }}>
      <Select
        multiple
        displayEmpty
        value={localValue}
        onChange={handleChange}
        onClose={handleClose}
        onOpen={handleOpen}
        open={isOpen}
        input={<OutlinedInput inputProps={{ 'aria-label': label }} />}
        renderValue={renderValue}
        sx={{
          color: localValue.length ? colors.primary.white : colors.brand.textMuted,
          '& .MuiSelect-select': { display: 'flex', alignItems: 'center', fontSize: '13px' },
        }}
        MenuProps={{ PaperProps: { sx: dropdownPaperSX } }}
      >
        {options.map((option) => {
          const isSelected = localValue.includes(option.value);
          const cfg = iconConfig?.[option.value];

          if (cfg) {
            const Icon = cfg.icon;
            return (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: isSelected ? `${cfg.color}30` : `${cfg.color}14`,
                      border: `1.5px solid ${isSelected ? cfg.color : 'transparent'}`,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Icon sx={{ color: cfg.color, fontSize: '14px' }} />
                  </Box>
                  <Box sx={{ color: colors.primary.white, fontSize: '13px', fontWeight: isSelected ? 600 : 400 }}>
                    {option.label}
                  </Box>
                </Box>
              </MenuItem>
            );
          }

          return (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox
                checked={isSelected}
                size="small"
                sx={{ color: colors.brand.textMuted, '&.Mui-checked': { color: colors.primary.yellow } }}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export const FiltersBar = ({ searchValue, onSearchChange, searchPlaceholder = 'Procure...' }: FiltersBarProps) => {
  const [localValue, setLocalValue] = useState(searchValue);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [localValue]);

  useEffect(() => {
    setLocalValue(searchValue);
  }, [searchValue]);

  return (
    <TextField
      size="small"
      placeholder={searchPlaceholder}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      inputProps={{ 'aria-label': searchPlaceholder }}
      sx={{ minWidth: 220, flex: '1 1 220px', ...filterFieldSX }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 17, color: `${colors.brand.textMuted} !important` }} />
          </InputAdornment>
        ),
      }}
    />
  );
};
